import type {
  AyzeoPromptPlatformRun,
  SectionFourConcern,
  SectionFourExecutionSuggestion,
  SectionFourIntentMatch,
  SectionFourResultValidity,
  SectionFourSuggestedValue,
  SectionFourUnsuitableReason,
  SectionFourVisibilityOutcome,
} from "./domain";
import type { ResolvedAuditPrompt } from "./section4-evidence";
import { isUnavailableRun } from "./section4-models";

const providerVersion = "stored-evidence-v1";

function fingerprintText(value: string) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

export function sectionFourRunFingerprint(run: AyzeoPromptPlatformRun, prompt: ResolvedAuditPrompt) {
  return fingerprintText(JSON.stringify({
    promptId: prompt.promptId,
    wording: prompt.wording,
    purpose: prompt.purpose,
    reasonForInclusion: prompt.reasonForInclusion,
    runId: run.id,
    payloadChecksum: run.payloadChecksum,
    rawAnswer: run.rawAnswer,
    availability: run.responseAvailability,
    missingReason: run.missingReason,
    mentioned: run.brandMentioned,
    citations: run.citations ?? run.citedUrls,
    mentions: run.mentionedEntities ?? run.mentionedBrands,
    sentiment: run.sentiment,
    position: run.resultPosition,
  }));
}

const human = <T extends string>(reason: string, evidenceIds: string[]): SectionFourSuggestedValue<T> => ({
  confidence: "Needs human judgement",
  reason,
  evidenceIds,
});

const supported = <T extends string>(
  value: T,
  confidence: "High confidence" | "Medium confidence",
  reason: string,
  evidenceIds: string[],
): SectionFourSuggestedValue<T> => ({ value, confidence, reason, evidenceIds });

function asksForProviderComparison(prompt: ResolvedAuditPrompt) {
  return /\b(who|which|recommend|consultant|company|companies|provider|help me)\b/i.test(
    `${prompt.wording} ${prompt.purpose}`,
  );
}

function brandedPurpose(prompt: ResolvedAuditPrompt) {
  return /\b(branded|brand baseline|brand understanding)\b/i.test(
    `${prompt.group} ${prompt.purpose} ${prompt.reasonForInclusion}`,
  );
}

export function createSectionFourExecutionSuggestion(
  run: AyzeoPromptPlatformRun,
  prompt: ResolvedAuditPrompt,
  generatedAt = new Date().toISOString(),
): SectionFourExecutionSuggestion {
  const evidenceIds = [run.id];
  const unavailable = isUnavailableRun(run);
  const entities = run.mentionedEntities?.length ?? run.mentionedBrands.length;
  const citations = run.citations?.length ?? run.citedUrls.length;
  const mentions = run.mentionCount ?? (run.brandMentioned ? 1 : 0);
  const noProviderEvidence = asksForProviderComparison(prompt)
    && run.brandMentioned === false
    && entities === 0
    && citations === 0
    && mentions === 0;

  const assessability: SectionFourSuggestedValue<"Yes, assessable"|"No, unsuitable"|"No result returned"> = unavailable
    ? supported<"No result returned">("No result returned", "High confidence", run.missingReason || "The stored execution is marked unavailable.", evidenceIds)
    : !run.rawAnswer.trim()
      ? supported<"No, unsuitable">("No, unsuitable", "High confidence", "No usable response text was returned.", evidenceIds)
      : noProviderEvidence
        ? supported<"No, unsuitable">("No, unsuitable", "Medium confidence", "The prompt asks for a provider comparison, but no provider, business, citation or mention was detected.", evidenceIds)
        : human<"Yes, assessable"|"No, unsuitable"|"No result returned">("A returned response alone does not prove that it is suitable for commercial visibility judgement.", evidenceIds);

  const unsuitableReason: SectionFourSuggestedValue<SectionFourUnsuitableReason> | undefined =
    assessability.value === "No result returned"
      ? undefined
      : !run.rawAnswer.trim()
        ? supported("Broken or unusable response", "High confidence", "The stored response text is empty.", evidenceIds)
        : noProviderEvidence
          ? supported("No provider comparison", "Medium confidence", "No provider or business comparison was detected for a comparison-style prompt.", evidenceIds)
          : undefined;

  const visibilityOutcome: SectionFourSuggestedValue<SectionFourVisibilityOutcome> =
    run.brandMentioned === false
      ? supported("Absent", "High confidence", "The stored result explicitly records that the business was not mentioned.", evidenceIds)
      : run.brandMentioned === true
        ? supported("Cited or mentioned only", "Medium confidence", "The stored result proves a mention or citation, but does not by itself prove a shortlist or recommendation.", evidenceIds)
        : human("The stored result does not supply a definitive business-mention state.", evidenceIds);

  const intentMatch: SectionFourSuggestedValue<SectionFourIntentMatch> = brandedPurpose(prompt)
    ? supported("Not applicable", "High confidence", "The prompt is recorded as a branded baseline rather than a non-branded customer-discovery test.", evidenceIds)
    : human("Customer-intent fit requires review of the exact answer against the prompt purpose.", evidenceIds);

  const concern: SectionFourSuggestedValue<SectionFourConcern> =
    run.evidenceLimitations?.length
      ? supported("Further research required", "Medium confidence", "The stored execution contains explicit evidence limitations.", evidenceIds)
      : human("Accuracy, omissions and conflicts require human review of the complete answer.", evidenceIds);

  const commercialSignificance = brandedPurpose(prompt)
    ? supported(
        "This execution concerns branded understanding and does not demonstrate non-branded commercial discovery.",
        "High confidence",
        "The stored prompt metadata identifies this as a branded baseline.",
        evidenceIds,
      )
    : human("No commercial-significance wording is generated without a direct, prompt-specific evidential basis.", evidenceIds);

  return {
    sourceRunId: run.id,
    promptSnapshotId: prompt.id,
    inputFingerprint: sectionFourRunFingerprint(run, prompt),
    generatedAt,
    provider: "Local evidence suggestion",
    providerVersion,
    assessability,
    unsuitableReason,
    visibilityOutcome,
    intentMatch,
    concern,
    commercialSignificance,
    limitations: [
      "Suggestions organise stored facts only and are not external AI analysis.",
      "Recommendation context, accuracy and commercial relevance remain Steve's decision.",
    ],
  };
}

export function legacyVisibilityOutcome(review?: SectionFourResultValidity): SectionFourVisibilityOutcome | undefined {
  if (review?.visibilityOutcome) return review.visibilityOutcome;
  if (review?.recommendationStrength === "Strong recommendation") return "Strong recommendation";
  if (review?.recommendationStrength === "Regular shortlist inclusion") return "Shortlist inclusion";
  if (review?.recommendationStrength === "Weak inclusion") return "Weak inclusion";
  if (review?.recommendationStrength === "Cited but not meaningfully recommended") return "Cited or mentioned only";
  if (review?.recommendationStrength === "Absent") return "Absent";
  return undefined;
}

export function legacyAssessability(review?: SectionFourResultValidity) {
  if (review?.assessability) return review.assessability;
  if (review?.status === "Assessable" || review?.status === "Valid for commercial visibility judgement") return "Yes, assessable" as const;
  if (review?.status === "Result unavailable") return "No result returned" as const;
  if (review?.status?.startsWith("Unsuitable") || review?.status === "Insufficient evidence") return "No, unsuitable" as const;
  return undefined;
}

export function legacyUnsuitableReason(review?: SectionFourResultValidity): SectionFourUnsuitableReason | undefined {
  if (review?.unsuitableReason) return review.unsuitableReason;
  const status = review?.status ?? "";
  if (status.includes("generic advice")) return "Generic or irrelevant advice";
  if (status.includes("no provider comparison")) return "No provider comparison";
  if (status.includes("geographical") || status.includes("wrong intent or geography")) return "Geographic drift";
  if (status.includes("misinterpreted")) return "Misinterpreted prompt";
  if (status === "Insufficient evidence") return "Insufficient evidence";
  if (status.includes("response failure")) return "Broken or unusable response";
  return undefined;
}

export function isFinalSectionFourRunDecision(run: AyzeoPromptPlatformRun, review?: SectionFourResultValidity) {
  if (isUnavailableRun(run, review)) return true;
  if (!review || (review.reviewState !== "Complete" && review.reviewState !== "Needs attention")) return false;
  const assessability = legacyAssessability(review);
  if (!assessability) return false;
  if (assessability === "No, unsuitable") return Boolean(legacyUnsuitableReason(review));
  if (assessability === "No result returned") return true;
  return Boolean(legacyVisibilityOutcome(review) && review.intentMatch && review.concern);
}

export function requiredSectionFourRunDecisions(run: AyzeoPromptPlatformRun, review?: SectionFourResultValidity) {
  if (isUnavailableRun(run, review)) return [];
  const missing: string[] = [];
  const assessability = legacyAssessability(review);
  if (!assessability) missing.push("assessability");
  if (assessability === "No, unsuitable" && !legacyUnsuitableReason(review)) missing.push("an unsuitable reason");
  if (assessability === "Yes, assessable") {
    if (!legacyVisibilityOutcome(review)) missing.push("visibility outcome");
    if (!review?.intentMatch) missing.push("customer-intent match");
    if (!review?.concern) missing.push("accuracy or concern");
  }
  return missing;
}
